/**
 * Memory growth probe -- samples heap + GPU/resource counts + edit-preview
 * workload hints at 2 Hz, tracks per-operation deltas via the commandBuffer,
 * and detects growth via trough-envelope regression (noise-resistant).
 *
 * See DebugMemoryPanel.tsx for the UI.
 */

import { getLiveEditPreviewForObject } from "../app/liveEditPreview.ts";
import { useStore } from "../app/store.ts";
import type { CommandType } from "../app/storeTypes.ts";
import { estimateRetainedBytes, getUndoManager } from "../app/storeUndo.ts";
import { getProbeCounts, resetRuntimeProbeActivity } from "./listenerProbe.ts";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const SAMPLE_MS = 500;
const RING_CAPACITY = 360; // 3 min at 2 Hz (localStorage budget)
const TROUGH_WINDOW = 20; // 10 s sliding-window min
const THEIL_SEN_MAX_POINTS = 300;
const SPIKE_MIN_HEAP_BYTES = 5 * 1024 * 1024;
const SPIKE_SIGMA = 3;
const OP_FLOOR_LEAD_SAMPLES = 10; // 5 s before start / after end for clean floor
export const MEMPROBE_SCHEMA_VERSION = 2;

/**
 * Scene-object bucket definitions. PoseRef objects don't use .name prefixes
 * for identification -- they're tagged via userData flags. Each bucket picks
 * out objects by a userData predicate so we can see which *class* of object
 * is growing (gizmo handles, wire overlays, selection outlines, etc.).
 * See scripts/build-mannequin.py and viewport/ helpers for where these flags
 * are assigned.
 */
const BUCKETS: Array<{
  key: string;
  match: (u: Record<string, unknown>) => boolean;
}> = [
  { key: "wireOverlay", match: (u) => u._wireOverlay === true },
  { key: "depthPrepass", match: (u) => u._depthPrepass === true },
  {
    key: "selectionOutline",
    match: (u) => u._selectionOutlinePrepass === true,
  },
  { key: "sketchOverlay", match: (u) => u.sketchOverlay === true },
  { key: "gizmoHandle", match: (u) => u.isGizmoHandle === true },
  { key: "excludeFromExport", match: (u) => u.excludeFromExport === true },
  { key: "poserefObject", match: (u) => typeof u.poserefObjectId === "string" },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Sample {
  schemaVersion: number;
  t: number;
  heapUsed: number;
  heapTotal: number;
  heapLimit: number;
  geometries: number;
  textures: number;
  drawCalls: number;
  renderCalls: number;
  triangles: number;
  sceneChildren: number;
  sceneTotal: number;
  meshCount: number;
  groupCount: number;
  lineCount: number;
  pointsCount: number;
  instancedMeshCount: number;
  materialCount: number;
  byTag: Record<string, number>;
  mode: string;
  op: CommandType | "idle";
  commandSource: "idle" | "gizmo" | "keyboard";
  listeners: number;
  rafPending: number;
  applyCommandLiveCalls: number;
  applyCommandLiveGizmoCalls: number;
  applyCommandLiveKeyboardCalls: number;
  applyCommandLiveEditCalls: number;
  applyCommandLiveObjectCalls: number;
  applyCommandLiveBoneCalls: number;
  commandEnterCalls: number;
  commandConfirmCalls: number;
  commandCancelCalls: number;
  commandConfirmLivePreviewCommitCalls: number;
  commandConfirmActiveEditSessionCommitCalls: number;
  liveEditPreviewPatchCalls: number;
  liveEditPreviewClearCalls: number;
  invalidateCalls: number;
  invalidateStoreCalls: number;
  invalidateCanvasPointerCalls: number;
  invalidateFlyPointerCalls: number;
  invalidateFlyKeyCalls: number;
  invalidateCameraKeyCalls: number;
  invalidateStoreSceneCalls: number;
  invalidateStoreEditSelectionCalls: number;
  invalidateStoreEditStateCalls: number;
  invalidateStoreEditHoverCalls: number;
  invalidateStoreCommandCalls: number;
  invalidateStoreCameraCalls: number;
  invalidateStoreCursorCalls: number;
  invalidateStoreUiCalls: number;
  invalidateCanvasPointerEditIdleCalls: number;
  invalidateCanvasPointerEditCommandCalls: number;
  invalidateCanvasPointerTransformCalls: number;
  invalidateCanvasPointerNavigateCalls: number;
  invalidateDirectCalls: number;
  invalidateOrbitControlsChangeCalls: number;
  invalidateCameraSyncCalls: number;
  invalidateCameraMovementCalls: number;
  invalidateViewLockCorrectionCalls: number;
  invalidateCameraTransitionCalls: number;
  editGranularity: string;
  editObjectVertexCount: number;
  editObjectCornerCount: number;
  editEdgeCount: number;
  editVisibleVertexCount: number;
  editDotCount: number;
  editHitDotCount: number;
  editFaceDotCount: number;
  editSelectionVertexCount: number;
  editSelectionEdgeCount: number;
  editSelectionFaceCount: number;
  proportionalVertexCount: number;
  activeEditSessionBytes: number;
  undoEntryCount: number;
  undoBytes: number;
  redoEntryCount: number;
  redoBytes: number;
  undoBudgetBytes: number;
  commandPreSnapshotBytes: number;
  commandCancelSnapshotBytes: number;
  estPreviewVertexCopyBytes: number;
  estPreviewExpandedPositionBytes: number;
  estPreviewDotMatrixBytes: number;
  estPreviewMinBytes: number;
}

export interface OpRecord {
  op: CommandType;
  tStart: number;
  tEnd: number;
  sampleIdxStart: number;
  sampleIdxEnd: number;
  heapAtStart: number;
  heapAtEnd: number;
}

export interface SpikeEvent {
  t: number;
  counter: string;
  delta: number;
  context: { mode: string; op: string };
}

export interface SlopeReport {
  slopePerSec: number;
  rSquared: number;
  nPoints: number;
  current: number;
  min: number;
  max: number;
}

export interface RateReport {
  perSec: number;
  delta: number;
  windowMs: number;
  current: number;
}

export interface OpSummary {
  op: string;
  count: number;
  meanFloorDelta: number;
  stderrFloorDelta: number;
  meanGeomDelta: number;
  meanTexDelta: number;
  meanMatDelta: number;
  meanSceneDelta: number;
}

export interface ProbeSnapshot {
  schemaVersion: number;
  active: boolean;
  startedAt: number | null;
  durationMs: number;
  sampleCount: number;
  opCount: number;
  spikeCount: number;
  slopes: Record<string, SlopeReport>;
  rates: Record<string, RateReport>;
  opStats: OpSummary[];
  recentSpikes: SpikeEvent[];
  latest: Sample | null;
  hasListenerProbe: boolean;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let active = false;
let startedAt: number | null = null;
let samples: Sample[] = [];
let ops: OpRecord[] = [];
let spikes: SpikeEvent[] = [];
let sampleTimer: ReturnType<typeof setInterval> | null = null;
let storeUnsub: (() => void) | null = null;
let currentOpStart: {
  op: CommandType;
  t: number;
  idx: number;
  heap: number;
} | null = null;
let cachedSnapshot: ProbeSnapshot | null = null;
let cachedSnapshotSig = -1;
const subs = new Set<() => void>();

// ---------------------------------------------------------------------------
// Persistence (localStorage crash recovery)
//
// Retains up to RING_CAPACITY samples (3 min). Flushed every 2 s while dirty
// and synchronously on beforeunload. On reload the raw arrays are restored so
// the panel can display pre-crash trends without restarting the probe.
// ---------------------------------------------------------------------------

const MEM_STORAGE_KEY = "poseref-debug-mem-probe";

interface PersistedProbeState {
  startedAt: number | null;
  samples: Sample[];
  ops: OpRecord[];
  spikes: SpikeEvent[];
}

let dirty = false;
const retainedBytesCache = new WeakMap<object, number>();

function createDefaultByTag(): Record<string, number> {
  return Object.fromEntries(BUCKETS.map((bucket) => [bucket.key, 0]));
}

function createEmptySample(t = 0): Sample {
  return {
    schemaVersion: MEMPROBE_SCHEMA_VERSION,
    t,
    heapUsed: 0,
    heapTotal: 0,
    heapLimit: 0,
    geometries: 0,
    textures: 0,
    drawCalls: 0,
    renderCalls: 0,
    triangles: 0,
    sceneChildren: 0,
    sceneTotal: 0,
    meshCount: 0,
    groupCount: 0,
    lineCount: 0,
    pointsCount: 0,
    instancedMeshCount: 0,
    materialCount: 0,
    byTag: createDefaultByTag(),
    mode: "unknown",
    op: "idle",
    commandSource: "idle",
    listeners: 0,
    rafPending: 0,
    applyCommandLiveCalls: 0,
    applyCommandLiveGizmoCalls: 0,
    applyCommandLiveKeyboardCalls: 0,
    applyCommandLiveEditCalls: 0,
    applyCommandLiveObjectCalls: 0,
    applyCommandLiveBoneCalls: 0,
    commandEnterCalls: 0,
    commandConfirmCalls: 0,
    commandCancelCalls: 0,
    commandConfirmLivePreviewCommitCalls: 0,
    commandConfirmActiveEditSessionCommitCalls: 0,
    liveEditPreviewPatchCalls: 0,
    liveEditPreviewClearCalls: 0,
    invalidateCalls: 0,
    invalidateStoreCalls: 0,
    invalidateCanvasPointerCalls: 0,
    invalidateFlyPointerCalls: 0,
    invalidateFlyKeyCalls: 0,
    invalidateCameraKeyCalls: 0,
    invalidateStoreSceneCalls: 0,
    invalidateStoreEditSelectionCalls: 0,
    invalidateStoreEditStateCalls: 0,
    invalidateStoreEditHoverCalls: 0,
    invalidateStoreCommandCalls: 0,
    invalidateStoreCameraCalls: 0,
    invalidateStoreCursorCalls: 0,
    invalidateStoreUiCalls: 0,
    invalidateCanvasPointerEditIdleCalls: 0,
    invalidateCanvasPointerEditCommandCalls: 0,
    invalidateCanvasPointerTransformCalls: 0,
    invalidateCanvasPointerNavigateCalls: 0,
    invalidateDirectCalls: 0,
    invalidateOrbitControlsChangeCalls: 0,
    invalidateCameraSyncCalls: 0,
    invalidateCameraMovementCalls: 0,
    invalidateViewLockCorrectionCalls: 0,
    invalidateCameraTransitionCalls: 0,
    editGranularity: "none",
    editObjectVertexCount: 0,
    editObjectCornerCount: 0,
    editEdgeCount: 0,
    editVisibleVertexCount: 0,
    editDotCount: 0,
    editHitDotCount: 0,
    editFaceDotCount: 0,
    editSelectionVertexCount: 0,
    editSelectionEdgeCount: 0,
    editSelectionFaceCount: 0,
    proportionalVertexCount: 0,
    activeEditSessionBytes: 0,
    undoEntryCount: 0,
    undoBytes: 0,
    redoEntryCount: 0,
    redoBytes: 0,
    undoBudgetBytes: 0,
    commandPreSnapshotBytes: 0,
    commandCancelSnapshotBytes: 0,
    estPreviewVertexCopyBytes: 0,
    estPreviewExpandedPositionBytes: 0,
    estPreviewDotMatrixBytes: 0,
    estPreviewMinBytes: 0,
  };
}

function normalizeSample(raw: unknown): Sample {
  if (!raw || typeof raw !== "object") return createEmptySample();
  const record = raw as Partial<Sample>;
  const fallback = createEmptySample(
    typeof record.t === "number" ? record.t : 0,
  );
  const byTag =
    record.byTag && typeof record.byTag === "object" ? record.byTag : {};
  return {
    ...fallback,
    ...record,
    schemaVersion:
      typeof record.schemaVersion === "number" ? record.schemaVersion : 1,
    byTag: {
      ...fallback.byTag,
      ...byTag,
    },
  };
}

function byteLengthOfView(value: unknown): number {
  return ArrayBuffer.isView(value) ? value.byteLength : 0;
}

function cachedRetainedBytes(value: unknown): number {
  if (!value || typeof value !== "object") return estimateRetainedBytes(value);
  const cached = retainedBytesCache.get(value);
  if (cached != null) return cached;
  const estimated = estimateRetainedBytes(value);
  retainedBytesCache.set(value, estimated);
  return estimated;
}

function editSessionByteLength(session: unknown): number {
  if (!session || typeof session !== "object") return 0;
  const record = session as Record<string, unknown>;
  return (
    byteLengthOfView(record.vertices) +
    byteLengthOfView(record.indices) +
    byteLengthOfView(record.loopNormals) +
    byteLengthOfView(record.uvs) +
    byteLengthOfView(record.skinRadii) +
    byteLengthOfView(record.baseVertices) +
    byteLengthOfView(
      (record.vertexPatch as Record<string, unknown> | undefined)?.indices,
    ) +
    byteLengthOfView(
      (record.vertexPatch as Record<string, unknown> | undefined)?.positions,
    ) +
    byteLengthOfView(
      (record.topology as Record<string, unknown> | undefined)?.corners,
    ) +
    byteLengthOfView(
      (record.topology as Record<string, unknown> | undefined)?.edges,
    ) +
    byteLengthOfView(
      (record.topology as Record<string, unknown> | undefined)?.offsets,
    ) +
    byteLengthOfView(
      (record.topology as Record<string, unknown> | undefined)?.edgeFlags,
    )
  );
}

function flushMemToStorage(): void {
  try {
    const state: PersistedProbeState = { startedAt, samples, ops, spikes };
    localStorage.setItem(MEM_STORAGE_KEY, JSON.stringify(state));
    dirty = false;
  } catch {
    /* quota or private browsing */
  }
}

function loadMemFromStorage(): void {
  try {
    const raw = localStorage.getItem(MEM_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as PersistedProbeState;
    if (!parsed || typeof parsed !== "object") return;
    samples = Array.isArray(parsed.samples)
      ? parsed.samples.map((sample) => normalizeSample(sample))
      : [];
    ops = Array.isArray(parsed.ops) ? parsed.ops : [];
    spikes = Array.isArray(parsed.spikes) ? parsed.spikes : [];
    startedAt = typeof parsed.startedAt === "number" ? parsed.startedAt : null;
    // active remains false — probe must be explicitly restarted after recovery
  } catch {
    /* malformed data — start fresh */
  }
}

loadMemFromStorage();
setInterval(() => {
  if (dirty) flushMemToStorage();
}, 2000);
window.addEventListener("beforeunload", flushMemToStorage);

// ---------------------------------------------------------------------------
// External store API (for useSyncExternalStore)
// ---------------------------------------------------------------------------

export function subscribe(fn: () => void): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

export function getSnapshot(): ProbeSnapshot {
  const sig =
    samples.length * 37 +
    ops.length * 13 +
    spikes.length * 7 +
    (active ? 1 : 0);
  if (cachedSnapshot && cachedSnapshotSig === sig) return cachedSnapshot;
  cachedSnapshot = buildSnapshot();
  cachedSnapshotSig = sig;
  return cachedSnapshot;
}

function emit(): void {
  cachedSnapshot = null;
  dirty = true;
  for (const fn of subs) fn();
}

// ---------------------------------------------------------------------------
// Public control
// ---------------------------------------------------------------------------

export function isProbeActive(): boolean {
  return active;
}

export function startProbe(): void {
  if (active) return;
  active = true;
  startedAt = Date.now();
  samples = [];
  ops = [];
  spikes = [];
  currentOpStart = null;
  resetRuntimeProbeActivity();
  sampleTimer = setInterval(takeSample, SAMPLE_MS);
  storeUnsub = useStore.subscribe(onStoreChange);
  emit();
  flushMemToStorage(); // persist cleared state so stale recovery data doesn't linger
}

export function stopProbe(): void {
  if (!active) return;
  active = false;
  if (sampleTimer) clearInterval(sampleTimer);
  sampleTimer = null;
  if (storeUnsub) storeUnsub();
  storeUnsub = null;
  currentOpStart = null;
  emit();
  flushMemToStorage();
}

export function resetProbe(): void {
  samples = [];
  ops = [];
  spikes = [];
  currentOpStart = null;
  startedAt = active ? Date.now() : null;
  resetRuntimeProbeActivity();
  emit();
  flushMemToStorage();
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

function takeSample(): void {
  const stats = window.__poseref_frameStats;
  const scene = window.__poseref_scene;
  const perfMem = (
    performance as unknown as {
      memory?: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    }
  ).memory;

  const byTag: Record<string, number> = Object.create(null);
  for (const b of BUCKETS) byTag[b.key] = 0;
  let sceneTotal = 0;
  let meshCount = 0;
  let groupCount = 0;
  let lineCount = 0;
  let pointsCount = 0;
  let instancedMeshCount = 0;
  let sceneChildren = 0;
  const uniqueMaterials = new Set<string>();

  if (scene) {
    sceneChildren = scene.children.length;
    scene.traverse((obj) => {
      sceneTotal++;
      const t = obj.type;
      if (t === "Mesh" || t === "SkinnedMesh") meshCount++;
      else if (t === "Group") groupCount++;
      else if (t === "Line" || t === "LineSegments" || t === "LineLoop")
        lineCount++;
      else if (t === "Points") pointsCount++;
      else if (t === "InstancedMesh" || t === "BatchedMesh")
        instancedMeshCount++;

      const mat = (obj as { material?: unknown }).material;
      if (mat) {
        if (Array.isArray(mat)) {
          for (const m of mat) {
            const uuid = (m as { uuid?: string } | null)?.uuid;
            if (uuid) uniqueMaterials.add(uuid);
          }
        } else {
          const uuid = (mat as { uuid?: string }).uuid;
          if (uuid) uniqueMaterials.add(uuid);
        }
      }

      const ud = obj.userData as Record<string, unknown> | undefined;
      if (ud) {
        for (const b of BUCKETS) {
          if (b.match(ud)) byTag[b.key]++;
        }
      }
    });
  }

  const state = useStore.getState();
  const undoManager = getUndoManager();
  const probeCounts = getProbeCounts();
  const commandSource: Sample["commandSource"] = state.commandBuffer
    ? state.commandBuffer.gizmoInitiated
      ? "gizmo"
      : "keyboard"
    : "idle";

  let editGranularity = "none";
  let editObjectVertexCount = 0;
  let editObjectCornerCount = 0;
  let editEdgeCount = 0;
  let editVisibleVertexCount = 0;
  let editDotCount = 0;
  let editHitDotCount = 0;
  let editFaceDotCount = 0;
  let editSelectionVertexCount = 0;
  let editSelectionEdgeCount = 0;
  let editSelectionFaceCount = 0;
  const proportionalVertexCount =
    state.commandBuffer?.proportionalWeights?.size ?? 0;
  let activeEditSessionBytes = 0;
  const undoEntryCount = undoManager.size;
  const undoBytes = undoManager.bytesUsed;
  const redoEntryCount = undoManager.redoSize;
  const redoBytes = undoManager.redoBytesUsed;
  const undoBudgetBytes = state.undoMemoryBudgetBytes;
  const commandPreSnapshotBytes = cachedRetainedBytes(state.commandPreSnapshot);
  const commandCancelSnapshotBytes = cachedRetainedBytes(
    state.commandCancelSnapshot,
  );
  let estPreviewVertexCopyBytes = 0;
  let estPreviewExpandedPositionBytes = 0;
  let estPreviewDotMatrixBytes = 0;
  let estPreviewMinBytes = 0;

  if (state.editMode && !state.editMode.isCurve) {
    const em = state.editMode;
    editGranularity = em.granularity;
    editSelectionVertexCount = em.selectedVertices.size;
    editSelectionEdgeCount = em.selectedEdges.size;
    editSelectionFaceCount = em.selectedFaces.size;
    const session =
      getLiveEditPreviewForObject(em.objectId) ??
      (state.activeEditSession?.objectId === em.objectId
        ? state.activeEditSession
        : null);
    const obj = state.sceneObjects.get(em.objectId);
    const vertices = session?.vertices ?? obj?.vertices ?? null;
    const topology = session?.topology ?? obj?.topology ?? null;
    activeEditSessionBytes = editSessionByteLength(session);
    if (vertices && topology) {
      editObjectVertexCount = vertices.length / 3;
      editObjectCornerCount = topology.corners.length;
      editEdgeCount = topology.edges.length / 2;
      editVisibleVertexCount = Math.max(
        0,
        editObjectVertexCount - em.hiddenVertices.size,
      );
      editDotCount = em.granularity === "vertex" ? editVisibleVertexCount : 0;
      editHitDotCount =
        em.granularity === "vertex" ? editVisibleVertexCount : 0;
      editFaceDotCount =
        em.granularity === "face"
          ? Math.max(0, topology.offsets.length - 1 - em.hiddenFaces.size)
          : 0;
      estPreviewVertexCopyBytes = vertices.byteLength;
      estPreviewExpandedPositionBytes = topology.corners.length * 3 * 4;
      estPreviewDotMatrixBytes = (editDotCount + editHitDotCount) * 16 * 4;
      estPreviewMinBytes =
        estPreviewVertexCopyBytes +
        estPreviewExpandedPositionBytes +
        estPreviewDotMatrixBytes;
    }
  }

  const s: Sample = {
    schemaVersion: MEMPROBE_SCHEMA_VERSION,
    t: Date.now(),
    heapUsed: perfMem?.usedJSHeapSize ?? stats?.jsHeapUsed ?? 0,
    heapTotal: perfMem?.totalJSHeapSize ?? stats?.jsHeapTotal ?? 0,
    heapLimit: perfMem?.jsHeapSizeLimit ?? stats?.jsHeapLimit ?? 0,
    geometries: stats?.geometries ?? 0,
    textures: stats?.textures ?? 0,
    drawCalls: stats?.drawCalls ?? 0,
    renderCalls: stats?.renderCalls ?? 0,
    triangles: stats?.triangles ?? 0,
    sceneChildren,
    sceneTotal,
    meshCount,
    groupCount,
    lineCount,
    pointsCount,
    instancedMeshCount,
    materialCount: uniqueMaterials.size,
    byTag,
    mode: String(state.mode ?? "?"),
    op: (state.commandBuffer?.type as CommandType) ?? "idle",
    commandSource,
    listeners: probeCounts.listeners,
    rafPending: probeCounts.rafPending,
    applyCommandLiveCalls: probeCounts.commandApplyCalls,
    applyCommandLiveGizmoCalls: probeCounts.commandApplyGizmoCalls,
    applyCommandLiveKeyboardCalls: probeCounts.commandApplyKeyboardCalls,
    applyCommandLiveEditCalls: probeCounts.commandApplyEditCalls,
    applyCommandLiveObjectCalls: probeCounts.commandApplyObjectCalls,
    applyCommandLiveBoneCalls: probeCounts.commandApplyBoneCalls,
    commandEnterCalls: probeCounts.commandEnterCalls,
    commandConfirmCalls: probeCounts.commandConfirmCalls,
    commandCancelCalls: probeCounts.commandCancelCalls,
    commandConfirmLivePreviewCommitCalls:
      probeCounts.commandConfirmLivePreviewCommitCalls,
    commandConfirmActiveEditSessionCommitCalls:
      probeCounts.commandConfirmActiveEditSessionCommitCalls,
    liveEditPreviewPatchCalls: probeCounts.liveEditPreviewPatchCalls,
    liveEditPreviewClearCalls: probeCounts.liveEditPreviewClearCalls,
    invalidateCalls: probeCounts.invalidateCalls,
    invalidateStoreCalls: probeCounts.invalidateStoreCalls,
    invalidateCanvasPointerCalls: probeCounts.invalidateCanvasPointerCalls,
    invalidateFlyPointerCalls: probeCounts.invalidateFlyPointerCalls,
    invalidateFlyKeyCalls: probeCounts.invalidateFlyKeyCalls,
    invalidateCameraKeyCalls: probeCounts.invalidateCameraKeyCalls,
    invalidateStoreSceneCalls: probeCounts.invalidateStoreSceneCalls,
    invalidateStoreEditSelectionCalls:
      probeCounts.invalidateStoreEditSelectionCalls,
    invalidateStoreEditStateCalls: probeCounts.invalidateStoreEditStateCalls,
    invalidateStoreEditHoverCalls: probeCounts.invalidateStoreEditHoverCalls,
    invalidateStoreCommandCalls: probeCounts.invalidateStoreCommandCalls,
    invalidateStoreCameraCalls: probeCounts.invalidateStoreCameraCalls,
    invalidateStoreCursorCalls: probeCounts.invalidateStoreCursorCalls,
    invalidateStoreUiCalls: probeCounts.invalidateStoreUiCalls,
    invalidateCanvasPointerEditIdleCalls:
      probeCounts.invalidateCanvasPointerEditIdleCalls,
    invalidateCanvasPointerEditCommandCalls:
      probeCounts.invalidateCanvasPointerEditCommandCalls,
    invalidateCanvasPointerTransformCalls:
      probeCounts.invalidateCanvasPointerTransformCalls,
    invalidateCanvasPointerNavigateCalls:
      probeCounts.invalidateCanvasPointerNavigateCalls,
    invalidateDirectCalls: probeCounts.invalidateDirectCalls,
    invalidateOrbitControlsChangeCalls:
      probeCounts.invalidateOrbitControlsChangeCalls,
    invalidateCameraSyncCalls: probeCounts.invalidateCameraSyncCalls,
    invalidateCameraMovementCalls: probeCounts.invalidateCameraMovementCalls,
    invalidateViewLockCorrectionCalls:
      probeCounts.invalidateViewLockCorrectionCalls,
    invalidateCameraTransitionCalls:
      probeCounts.invalidateCameraTransitionCalls,
    editGranularity,
    editObjectVertexCount,
    editObjectCornerCount,
    editEdgeCount,
    editVisibleVertexCount,
    editDotCount,
    editHitDotCount,
    editFaceDotCount,
    editSelectionVertexCount,
    editSelectionEdgeCount,
    editSelectionFaceCount,
    proportionalVertexCount,
    activeEditSessionBytes,
    undoEntryCount,
    undoBytes,
    redoEntryCount,
    redoBytes,
    undoBudgetBytes,
    commandPreSnapshotBytes,
    commandCancelSnapshotBytes,
    estPreviewVertexCopyBytes,
    estPreviewExpandedPositionBytes,
    estPreviewDotMatrixBytes,
    estPreviewMinBytes,
  };

  samples.push(s);
  if (samples.length > RING_CAPACITY) samples.shift();

  detectSpikes(s);
  emit();
}

// ---------------------------------------------------------------------------
// Command buffer observer -- per-op delta tracking
// ---------------------------------------------------------------------------

function onStoreChange(): void {
  const cb = useStore.getState().commandBuffer;
  const nowOp = cb?.type ?? null;

  if (nowOp && !currentOpStart) {
    const idx = samples.length;
    const heap = samples.length > 0 ? samples[samples.length - 1].heapUsed : 0;
    currentOpStart = { op: nowOp, t: Date.now(), idx, heap };
  } else if (!nowOp && currentOpStart) {
    const idx = samples.length;
    const heap = samples.length > 0 ? samples[samples.length - 1].heapUsed : 0;
    ops.push({
      op: currentOpStart.op,
      tStart: currentOpStart.t,
      tEnd: Date.now(),
      sampleIdxStart: currentOpStart.idx,
      sampleIdxEnd: idx,
      heapAtStart: currentOpStart.heap,
      heapAtEnd: heap,
    });
    currentOpStart = null;
    emit();
  }
}

// ---------------------------------------------------------------------------
// Spike detection -- record step changes without discarding them
// ---------------------------------------------------------------------------

function detectSpikes(s: Sample): void {
  if (samples.length < 2) return;
  const prev = samples[samples.length - 2];
  const dHeap = s.heapUsed - prev.heapUsed;
  if (Math.abs(dHeap) >= SPIKE_MIN_HEAP_BYTES) {
    pushSpike({
      t: s.t,
      counter: "heapUsed",
      delta: dHeap,
      context: { mode: s.mode, op: s.op },
    });
  }
  const dUndoBytes = s.undoBytes - prev.undoBytes;
  if (Math.abs(dUndoBytes) >= SPIKE_MIN_HEAP_BYTES) {
    pushSpike({
      t: s.t,
      counter: "undoBytes",
      delta: dUndoBytes,
      context: { mode: s.mode, op: s.op },
    });
  }
  const dPreSnapshotBytes =
    s.commandPreSnapshotBytes - prev.commandPreSnapshotBytes;
  if (Math.abs(dPreSnapshotBytes) >= SPIKE_MIN_HEAP_BYTES) {
    pushSpike({
      t: s.t,
      counter: "commandPreSnapshotBytes",
      delta: dPreSnapshotBytes,
      context: { mode: s.mode, op: s.op },
    });
  }

  // 3-sigma jumps in listener / rAF / scene counts
  if (samples.length >= 30) {
    const recent = samples.slice(-30);
    checkSigma(recent, s, "listeners", (x) => x.listeners);
    checkSigma(recent, s, "rafPending", (x) => x.rafPending);
    checkSigma(recent, s, "sceneTotal", (x) => x.sceneTotal);
    checkSigma(recent, s, "undoBytes", (x) => x.undoBytes);
  }
}

function checkSigma(
  recent: Sample[],
  s: Sample,
  name: string,
  pick: (x: Sample) => number,
): void {
  const vals = recent.map(pick);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  let sq = 0;
  for (const v of vals) sq += (v - mean) * (v - mean);
  const stdev = Math.sqrt(sq / vals.length);
  const cur = pick(s);
  if (stdev > 0 && Math.abs(cur - mean) > SPIKE_SIGMA * stdev) {
    pushSpike({
      t: s.t,
      counter: name,
      delta: cur - mean,
      context: { mode: s.mode, op: s.op },
    });
  }
}

function pushSpike(ev: SpikeEvent): void {
  spikes.push(ev);
  if (spikes.length > 200) spikes.shift();
}

// ---------------------------------------------------------------------------
// Trough envelope + Theil-Sen slope
//
// Trough envelope = causal sliding-window minimum. This strips GC sawtooth
// peaks and leaves the working-set floor. Linear growth of the floor is the
// leak signal.
//
// Theil-Sen is a robust (outlier-resistant) slope estimator: median of all
// pairwise slopes. R^2 is computed against the Theil-Sen line for a fit
// quality read.
// ---------------------------------------------------------------------------

function troughEnvelope(pick: (s: Sample) => number): number[] {
  const out: number[] = [];
  for (let i = 0; i < samples.length; i++) {
    const lo = Math.max(0, i - TROUGH_WINDOW + 1);
    let m = pick(samples[lo]);
    for (let j = lo + 1; j <= i; j++) {
      const v = pick(samples[j]);
      if (v < m) m = v;
    }
    out.push(m);
  }
  return out;
}

function theilSen(
  ys: number[],
  xs: number[],
): { slope: number; intercept: number } {
  const n = ys.length;
  if (n < 2) return { slope: 0, intercept: ys[0] ?? 0 };
  const slopes: number[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = xs[j] - xs[i];
      if (dx === 0) continue;
      slopes.push((ys[j] - ys[i]) / dx);
    }
  }
  slopes.sort((a, b) => a - b);
  const slope = slopes.length === 0 ? 0 : slopes[slopes.length >> 1];

  const medX = median(xs);
  const medY = median(ys);
  const intercept = medY - slope * medX;
  return { slope, intercept };
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const s = [...arr].sort((a, b) => a - b);
  return s[s.length >> 1];
}

function slopeReport(pick: (s: Sample) => number): SlopeReport {
  if (samples.length < 10) {
    const cur = samples.length > 0 ? pick(samples[samples.length - 1]) : 0;
    return {
      slopePerSec: 0,
      rSquared: 0,
      nPoints: samples.length,
      current: cur,
      min: cur,
      max: cur,
    };
  }

  const env = troughEnvelope(pick);
  const cur = pick(samples[samples.length - 1]);
  let min = env[0];
  let max = env[0];
  for (const v of env) {
    if (v < min) min = v;
    if (v > max) max = v;
  }

  // Downsample to THEIL_SEN_MAX_POINTS to bound cost
  const stride = Math.max(1, Math.ceil(env.length / THEIL_SEN_MAX_POINTS));
  const xs: number[] = [];
  const ys: number[] = [];
  const t0 = samples[0].t;
  for (let i = 0; i < env.length; i += stride) {
    xs.push((samples[i].t - t0) / 1000); // seconds
    ys.push(env[i]);
  }

  const { slope, intercept } = theilSen(ys, xs);

  // R^2 of the Theil-Sen fit
  const meanY = ys.reduce((a, b) => a + b, 0) / ys.length;
  let ssTot = 0;
  let ssRes = 0;
  for (let i = 0; i < ys.length; i++) {
    const pred = slope * xs[i] + intercept;
    ssRes += (ys[i] - pred) * (ys[i] - pred);
    ssTot += (ys[i] - meanY) * (ys[i] - meanY);
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;

  return {
    slopePerSec: slope,
    rSquared: Math.max(0, Math.min(1, r2)),
    nPoints: ys.length,
    current: cur,
    min,
    max,
  };
}

function recentRateReport(pick: (s: Sample) => number): RateReport {
  if (samples.length < 2) {
    const cur =
      samples.length > 0 ? Number(pick(samples[samples.length - 1]) || 0) : 0;
    return { perSec: 0, delta: 0, windowMs: 0, current: cur };
  }
  const windowSize = Math.min(10, samples.length);
  const first = samples[samples.length - windowSize];
  const last = samples[samples.length - 1];
  const firstValue = Number(pick(first) || 0);
  const lastValue = Number(pick(last) || 0);
  const delta = lastValue - firstValue;
  const windowMs = Math.max(1, last.t - first.t);
  return {
    perSec: delta / (windowMs / 1000),
    delta,
    windowMs,
    current: lastValue,
  };
}

// ---------------------------------------------------------------------------
// Per-op summary: mean floor-delta ± stderr, per op type
//
// For each completed op record, read the floor value OP_FLOOR_LEAD_SAMPLES
// before start and after end (stripping the noisy in-op window). The mean
// delta across N ops of the same type, with stderr, quantifies "leak per op".
// ---------------------------------------------------------------------------

interface OpDeltas {
  floor: number;
  geom: number;
  tex: number;
  mat: number;
  scene: number;
}

function computeOpStats(): OpSummary[] {
  if (samples.length === 0 || ops.length === 0) return [];
  const heapFloor = troughEnvelope((s) => s.heapUsed);

  const byOp = new Map<string, OpDeltas[]>();
  for (const op of ops) {
    const preIdx = Math.max(0, op.sampleIdxStart - OP_FLOOR_LEAD_SAMPLES);
    const postIdx = Math.min(
      samples.length - 1,
      op.sampleIdxEnd + OP_FLOOR_LEAD_SAMPLES,
    );
    if (postIdx <= preIdx) continue;
    // Integer counts: use samples flanking the op directly -- no trough needed.
    const preSample = samples[Math.max(0, op.sampleIdxStart - 1)];
    const postSample = samples[Math.min(samples.length - 1, op.sampleIdxEnd)];
    const d: OpDeltas = {
      floor: heapFloor[postIdx] - heapFloor[preIdx],
      geom: postSample.geometries - preSample.geometries,
      tex: postSample.textures - preSample.textures,
      mat: postSample.materialCount - preSample.materialCount,
      scene: postSample.sceneTotal - preSample.sceneTotal,
    };
    const arr = byOp.get(op.op) ?? [];
    arr.push(d);
    byOp.set(op.op, arr);
  }

  const out: OpSummary[] = [];
  for (const [op, deltas] of byOp) {
    const n = deltas.length;
    const sum = (pick: (d: OpDeltas) => number) =>
      deltas.reduce((a, d) => a + pick(d), 0);
    const meanFloor = sum((d) => d.floor) / n;
    let variance = 0;
    for (const d of deltas)
      variance += (d.floor - meanFloor) * (d.floor - meanFloor);
    variance = n > 1 ? variance / (n - 1) : 0;
    const stderr = n > 0 ? Math.sqrt(variance / n) : 0;
    out.push({
      op,
      count: n,
      meanFloorDelta: meanFloor,
      stderrFloorDelta: stderr,
      meanGeomDelta: sum((d) => d.geom) / n,
      meanTexDelta: sum((d) => d.tex) / n,
      meanMatDelta: sum((d) => d.mat) / n,
      meanSceneDelta: sum((d) => d.scene) / n,
    });
  }
  // Sort by the most informative leak indicator: prefer geom delta on browsers
  // where heap is blind (Safari), fall back to floor otherwise.
  out.sort(
    (a, b) =>
      b.meanGeomDelta - a.meanGeomDelta || b.meanFloorDelta - a.meanFloorDelta,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

function buildSnapshot(): ProbeSnapshot {
  const latest = samples.length > 0 ? samples[samples.length - 1] : null;
  const slopes: Record<string, SlopeReport> = {
    heapUsed: slopeReport((s) => s.heapUsed),
    geometries: slopeReport((s) => s.geometries),
    textures: slopeReport((s) => s.textures),
    materials: slopeReport((s) => s.materialCount),
    sceneTotal: slopeReport((s) => s.sceneTotal),
    meshes: slopeReport((s) => s.meshCount),
    groups: slopeReport((s) => s.groupCount),
    lines: slopeReport((s) => s.lineCount),
    points: slopeReport((s) => s.pointsCount),
    instanced: slopeReport((s) => s.instancedMeshCount),
    listeners: slopeReport((s) => s.listeners),
    rafPending: slopeReport((s) => s.rafPending),
    undoBytes: slopeReport((s) => s.undoBytes),
    redoBytes: slopeReport((s) => s.redoBytes),
    undoEntries: slopeReport((s) => s.undoEntryCount),
    redoEntries: slopeReport((s) => s.redoEntryCount),
  };
  for (const b of BUCKETS) {
    slopes[`byTag.${b.key}`] = slopeReport((s) => s.byTag[b.key] ?? 0);
  }
  const rates: Record<string, RateReport> = {
    renderCalls: recentRateReport((s) => s.renderCalls),
    applyCommandLive: recentRateReport((s) => s.applyCommandLiveCalls),
    applyCommandLiveGizmo: recentRateReport(
      (s) => s.applyCommandLiveGizmoCalls,
    ),
    applyCommandLiveKeyboard: recentRateReport(
      (s) => s.applyCommandLiveKeyboardCalls,
    ),
    commandEnter: recentRateReport((s) => s.commandEnterCalls),
    commandConfirm: recentRateReport((s) => s.commandConfirmCalls),
    commandCancel: recentRateReport((s) => s.commandCancelCalls),
    commandConfirmLivePreviewCommit: recentRateReport(
      (s) => s.commandConfirmLivePreviewCommitCalls,
    ),
    commandConfirmActiveEditSessionCommit: recentRateReport(
      (s) => s.commandConfirmActiveEditSessionCommitCalls,
    ),
    liveEditPreviewPatch: recentRateReport((s) => s.liveEditPreviewPatchCalls),
    liveEditPreviewClear: recentRateReport((s) => s.liveEditPreviewClearCalls),
    invalidateCalls: recentRateReport((s) => s.invalidateCalls),
    invalidateStore: recentRateReport((s) => s.invalidateStoreCalls),
    invalidateCanvasPointer: recentRateReport(
      (s) => s.invalidateCanvasPointerCalls,
    ),
    invalidateCanvasPointerEditIdle: recentRateReport(
      (s) => s.invalidateCanvasPointerEditIdleCalls,
    ),
    invalidateCanvasPointerEditCommand: recentRateReport(
      (s) => s.invalidateCanvasPointerEditCommandCalls,
    ),
    invalidateCanvasPointerTransform: recentRateReport(
      (s) => s.invalidateCanvasPointerTransformCalls,
    ),
    invalidateCanvasPointerNavigate: recentRateReport(
      (s) => s.invalidateCanvasPointerNavigateCalls,
    ),
    invalidateFlyPointer: recentRateReport((s) => s.invalidateFlyPointerCalls),
    invalidateFlyKey: recentRateReport((s) => s.invalidateFlyKeyCalls),
    invalidateCameraKey: recentRateReport((s) => s.invalidateCameraKeyCalls),
    invalidateStoreScene: recentRateReport((s) => s.invalidateStoreSceneCalls),
    invalidateStoreEditSelection: recentRateReport(
      (s) => s.invalidateStoreEditSelectionCalls,
    ),
    invalidateStoreEditState: recentRateReport(
      (s) => s.invalidateStoreEditStateCalls,
    ),
    invalidateStoreEditHover: recentRateReport(
      (s) => s.invalidateStoreEditHoverCalls,
    ),
    invalidateStoreCommand: recentRateReport(
      (s) => s.invalidateStoreCommandCalls,
    ),
    invalidateStoreCamera: recentRateReport(
      (s) => s.invalidateStoreCameraCalls,
    ),
    invalidateStoreCursor: recentRateReport(
      (s) => s.invalidateStoreCursorCalls,
    ),
    invalidateStoreUi: recentRateReport((s) => s.invalidateStoreUiCalls),
    invalidateDirect: recentRateReport((s) => s.invalidateDirectCalls),
    invalidateOrbitControlsChange: recentRateReport(
      (s) => s.invalidateOrbitControlsChangeCalls,
    ),
    invalidateCameraSync: recentRateReport((s) => s.invalidateCameraSyncCalls),
    invalidateCameraMovement: recentRateReport(
      (s) => s.invalidateCameraMovementCalls,
    ),
    invalidateViewLockCorrection: recentRateReport(
      (s) => s.invalidateViewLockCorrectionCalls,
    ),
    invalidateCameraTransition: recentRateReport(
      (s) => s.invalidateCameraTransitionCalls,
    ),
  };
  return {
    schemaVersion: MEMPROBE_SCHEMA_VERSION,
    active,
    startedAt,
    durationMs: startedAt ? Date.now() - startedAt : 0,
    sampleCount: samples.length,
    opCount: ops.length,
    spikeCount: spikes.length,
    slopes,
    rates,
    opStats: computeOpStats(),
    recentSpikes: spikes.slice(-20),
    latest,
    hasListenerProbe: getProbeCounts().installed,
  };
}

// ---------------------------------------------------------------------------
// Serialization for sharing
// ---------------------------------------------------------------------------

export function serializeSummary(): string {
  const snap = getSnapshot();
  const payload = {
    schemaVersion: snap.schemaVersion,
    startedAt: snap.startedAt,
    durationMs: snap.durationMs,
    sampleCount: snap.sampleCount,
    opCount: snap.opCount,
    spikeCount: snap.spikeCount,
    hasListenerProbe: snap.hasListenerProbe,
    slopes: snap.slopes,
    rates: snap.rates,
    opStats: snap.opStats,
    latest: snap.latest,
    recentSpikes: snap.recentSpikes,
  };
  return JSON.stringify(payload, null, 2);
}

export function serializeJSONL(): string {
  return samples.map((s) => JSON.stringify(s)).join("\n");
}

export function downloadExport(
  content: string,
  filename: string,
  mime = "application/json",
): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
